import { Box, Grid, Link, Typography } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { useFormik } from 'formik';
import { useMemo, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { MissingControlPersonErrorMessage } from './MissingControlPersonErrorMessage';
import { LegalDocumentKey } from '../../../../domain/config';
import { isSmallBusiness } from '../../../../domain/qualification';
import { AddressView } from '../../../../general/components/address-view';
import { DetailsAccordion } from '../../../../general/components/details-accordion';
import { RequiredText } from '../../../../general/components/required-text';
import { FormikToggle } from '../../../../general/inputs/formik-toggle';
import { formatCentsCurrency } from '../../../../general/utils/formatters';
import { OtherIndividuals } from '../../../application-review/components/other-individuals';
import { DetailsRow, ReviewDetailsGrid } from '../../../application-review/components/review-grid';
import { selectOtherIndividuals } from '../../../application-review/reducers/other-individuals.reducer';
import { recordConsent, selectConsent } from '../../../legal-consent/reducers/consent';
import { cardProgramTermsSelector } from '../../../legal-consent/selectors/legal-document-selectors';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';
import { selectBusinessInfo } from '../../reducers/business-info';
import { nextStep, selectnextStep } from '../../reducers/next-step';
import { selectPersonalInfo, updatePersonalInfoOwnershipAndControl } from '../../reducers/personalInfo';

const selector = createSelector(
  [
    selectnextStep,
    selectOtherIndividuals,
    cardProgramTermsSelector,
    selectPersonalInfo,
    selectBusinessInfo,
    selectConsent,
  ],
  (nextStep, otherIndividuals, cardProgramTerms, personalInfo, { businessInfo: business }, consent) => ({
    personalInfo,
    business,
    cardProgramTerms,
    saving: consent.loading || nextStep.loading,
    otherIndividuals,
  }),
);

const consentCardTermsSchema = yup.mixed().oneOf([true], 'You must read and accept the Card Program Terms');
const consentOfferSummarySchema = yup.mixed().oneOf([true], 'You must read and accept the Coast Offer Summary');

const validationSchemaWithOfferSummary = yup.object({
  consentCardTerms: consentCardTermsSchema,
  consentOfferSummary: consentOfferSummarySchema,
});

const validationSchemaCardTermsOnly = yup.object({
  consentCardTerms: consentCardTermsSchema,
});

type UbosFormProps = {
  submitLabel: string;
  attestationVerb: string;
  submitAction: 'get_ubos' | 'pre_collect_ubos';
  offerSummaryDoc?: { url: string };
};

const UbosForm: FC<UbosFormProps> = ({ submitLabel, attestationVerb, submitAction, offerSummaryDoc }) => {
  const [isControlPersonError, setIsControlPersonError] = useState(false);
  const dispatch = useDispatch();
  const { personalInfo, business, cardProgramTerms, saving, otherIndividuals } = useSelector(selector);

  const showOfferSummaryConsent = !!offerSummaryDoc;

  const isOtherIndividualControlPerson = useMemo(() => {
    return otherIndividuals.some((individual) => individual.controlPerson);
  }, [otherIndividuals]);
  const isApplicantControlPerson = personalInfo.controlPerson;

  const formik = useFormik({
    initialValues: {
      personalInfo: {
        ...personalInfo,
      },
      consentCardTerms: false,
      consentOfferSummary: false,
    },
    validationSchema: showOfferSummaryConsent ? validationSchemaWithOfferSummary : validationSchemaCardTermsOnly,
    validate: (values) => {
      const hasControlPerson =
        Boolean(values.personalInfo.controlPerson) || isApplicantControlPerson || isOtherIndividualControlPerson;
      const errors: Record<string, boolean> = {};

      if (!hasControlPerson) {
        setIsControlPersonError(true);
        errors.missingControlPerson = true;
      } else {
        setIsControlPersonError(false);
        delete errors.missingControlPerson;
      }

      return errors;
    },
    validateOnChange: false,
    onSubmit: (values) => {
      const documentKeys: LegalDocumentKey[] = showOfferSummaryConsent
        ? ['card_program_terms', 'commercial_financing_disclosure']
        : ['card_program_terms'];
      dispatch(
        recordConsent({
          documentKeys,
          onSuccess: [
            updatePersonalInfoOwnershipAndControl({
              beneficialOwner: values.personalInfo.beneficialOwner,
              controlPerson: values.personalInfo.controlPerson,
            }),
            nextStep({
              action: submitAction,
              gotoNextPage: true,
            }),
          ],
        }),
      );
    },
  });

  const businessDetails: DetailsRow[] = useMemo(() => {
    return [
      { label: 'Business Name', value: business.name },
      { label: 'Type', value: business.businessType },
      {
        label: 'Address',
        value: <AddressView address={business.address} isPublic />,
      },
      {
        label: 'Annual Business Revenue',
        value: formatCentsCurrency(business.annualRevenue ?? 0),
      },
      {
        label: 'Monthly Fuel Spend',
        value: formatCentsCurrency(business.monthlyFuelSpend ?? 0),
      },
      {
        label: 'Fleet Size',
        value: business.fleetSize,
      },
    ];
  }, [business]);

  const smallBusiness = isSmallBusiness(business);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <OtherIndividuals showApplicant={smallBusiness} includePhone={true} />
        </Grid>
        <Grid item>
          <DetailsAccordion
            title="Business"
            details={<ReviewDetailsGrid details={businessDetails} label="Business" />}
          />
        </Grid>
        <Grid item>
          <FormikToggle
            formik={formik}
            id="consentCardTerms"
            name="consentCardTerms"
            label={
              <RequiredText>
                I agree to the Coast{' '}
                <Link href={cardProgramTerms?.url} target="_blank">
                  Card Program Terms
                </Link>
              </RequiredText>
            }
            toggleStyle="checkbox"
          />
          {offerSummaryDoc && (
            <FormikToggle
              formik={formik}
              id="consentOfferSummary"
              name="consentOfferSummary"
              label={
                <RequiredText>
                  I have reviewed and acknowledge receipt of the{' '}
                  <Link href={offerSummaryDoc.url} target="_blank">
                    Coast Offer Summary
                  </Link>
                </RequiredText>
              }
              toggleStyle="checkbox"
            />
          )}

          <Box>
            <Typography variant="body2">
              By {attestationVerb}, I attest that the information provided is accurate to the best of my knowledge.
            </Typography>
          </Box>
          {isControlPersonError && (
            <Box mt={2}>
              <MissingControlPersonErrorMessage />
            </Box>
          )}
          <SubmitWorkflowButton type="submit" progress={saving} sx={{ mt: 4 }}>
            {submitLabel}
          </SubmitWorkflowButton>
        </Grid>
      </Grid>
    </form>
  );
};

export { UbosForm };
export type { UbosFormProps };
